import type {Camera, Object3D, Vector2} from 'three';
import {NodeBuilder, SpriteNodeMaterial, SpriteNodeMaterialParameters, type Node} from 'three/webgpu';
import type {Subscription} from 'rxjs';
import {
    float,
    materialRotation,
    modelViewMatrix,
    modelWorldMatrix,
    positionGeometry,
    reference,
    rotate,
    uniform,
    vec2,
    vec3,
    vec4
} from 'three/tsl';
import {CameraEventName, type CameraProjectionTypeChangedEventPayload} from '../eventBus';
import {useEvent} from '../hooks';

/**
 * 精灵节点构建器的运行时结构。
 *
 * three 的运行时 NodeBuilder 会携带 camera，Sprite 对象也会携带 center；
 * 但当前类型声明未完整暴露这些字段，因此在本文件集中收窄，避免调用处散落断言。
 */
type CameraSpriteNodeBuilder = NodeBuilder & {
    /** 当前构建精灵节点时使用的相机。 */
    camera: Camera;
    /** 当前构建精灵节点时使用的对象。 */
    object: Object3D & {
        /** Sprite.center 运行时属性，用于屏幕对齐。 */
        readonly center?: Vector2 & {
            /** Vector2 运行时标记。 */
            readonly isVector2?: boolean;
        };
    };
};

/**
 * 根据当前相机投影类型计算屏幕尺寸的精灵节点材质。
 */
export class CameraSpriteNodeMaterial extends SpriteNodeMaterial {
    /** 当前相机是否为透视相机的 TSL uniform。 */
    public isPerspectiveCameraUniform = uniform(false);

    /** CAD 事件总线操作集合。 */
    private readonly eventBus = useEvent();

    /** 相机投影类型变化事件订阅。 */
    private readonly cameraProjectionTypeChangedSubscription: Subscription;

    /**
     * 创建相机感知的精灵节点材质。
     *
     * @param parameters 精灵节点材质参数。
     */
    constructor(parameters: SpriteNodeMaterialParameters) {
        super(parameters);

        // 材质只订阅投影状态事件，不直接持有 CameraManager 或相机实例，避免生命周期耦合。
        this.cameraProjectionTypeChangedSubscription = this.eventBus.add(
            CameraEventName.ProjectionTypeChanged,
            this.handleCameraProjectionTypeChangedEvent,
        );
    }

    /**
     * 释放材质及其事件订阅。
     */
    dispose(): void {
        this.cameraProjectionTypeChangedSubscription.unsubscribe();
        super.dispose();
    }

    /**
     * 根据相机投影类型变化事件同步 TSL uniform。
     *
     * @param payload 相机投影类型变化事件载荷。
     */
    private readonly handleCameraProjectionTypeChanged = (
        payload: CameraProjectionTypeChangedEventPayload,
    ): void => {
        // boolean uniform 只需要更新 value，TSL 会在后续渲染时读取最新值。
        this.isPerspectiveCameraUniform.value = payload.isPerspectiveCamera;
    };

    /**
     * 处理事件总线的弱类型事件载荷。
     *
     * @param payload 相机投影类型变化事件载荷。
     */
    private readonly handleCameraProjectionTypeChangedEvent = (
        payload: unknown,
    ): void => {
        this.handleCameraProjectionTypeChanged(payload as CameraProjectionTypeChangedEventPayload);
    };

    /**
     * 构建精灵在视图空间中的位置节点。
     *
     * @param builder 节点构建器。
     * @returns 视图空间位置节点。
     */
    setupPositionView(builder: NodeBuilder): Node {
        // three 运行时 NodeBuilder 带有 camera，但类型声明暂未覆盖；这里统一收窄后再接入 TSL reference。
        const {object, camera} = builder as CameraSpriteNodeBuilder;
        const {positionNode, rotationNode, scaleNode, sizeAttenuation, isPerspectiveCameraUniform} = this;
        const mvPosition = modelViewMatrix.mul(positionNode ? vec3(positionNode) : vec3(0));
        let scale = vec2(modelWorldMatrix[0].xyz.length(), modelWorldMatrix[1].xyz.length());
        if (scaleNode !== null) {
            scale = scale.mul(vec2(scaleNode)) as typeof scale;
        }
        if (sizeAttenuation === false) {
            // 关闭尺寸衰减时，透视与正交相机需要使用不同的缩放补偿公式。
            const cameraTop = reference('top', 'float', camera);
            const cameraBottom = reference('bottom', 'float', camera);
            const cameraZoom = reference('zoom', 'float', camera);

            const perspectiveScale = scale.mul(mvPosition.z.negate());

            const orthographicScale = scale
                .mul(cameraTop.sub(cameraBottom))
                .div(cameraZoom);

            // 使用 TSL select 让 shader 根据相机投影 uniform 选择缩放结果。
            scale = isPerspectiveCameraUniform.select(perspectiveScale, orthographicScale) as typeof scale;
        }

        let alignedPosition = positionGeometry.xy;
        if (object.center && object.center.isVector2 === true) {
            // Sprite.center 是运行时对象属性，通过 reference 接入节点系统。
            const center = reference('center', 'vec2', object);
            alignedPosition = alignedPosition.sub(center.sub(0.5));
        }
        alignedPosition = alignedPosition.mul(scale);
        /**
         * Three.js 运行时与 SpriteNodeMaterial 文档都约定 rotationNode 为 float 节点，
         * 但当前 @types/three 将该属性宽泛声明为 Node，导致 float() 无法匹配标量重载。
         * 类型收窄只发生在继承边界，不改变 TSL 节点图和精灵旋转的运行时行为。
         */
        const rotationSource = (rotationNode ?? materialRotation) as Node<'float'>;
        const rotation = float(rotationSource);
        const rotatedPosition = rotate(alignedPosition, rotation);
        return vec4(mvPosition.xy.add(rotatedPosition), mvPosition.zw);
    }
}
